import React, { useMemo, useState } from 'react';
import {
  Box,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import axiosInstance from '../../api/axiosConfig';

const AiAdmissionsChatbot = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Hi, I can help with admissions, documents, fee process, and transport questions.',
    },
  ]);

  const schoolInfo = useMemo(
    () => ({
      schoolName: localStorage.getItem('schoolName') || 'Skhool',
      phone: localStorage.getItem('schoolPhone') || '',
      email: localStorage.getItem('schoolEmail') || '',
      address: localStorage.getItem('schoolAddress') || '',
      board: localStorage.getItem('schoolAffiliationBoard') || '',
    }),
    []
  );

  const send = async () => {
    const trimmed = question.trim();
    if (!trimmed || sending) return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setQuestion('');
    setSending(true);

    try {
      const response = await axiosInstance.post('/api/ai-assistant/admissions-chat', {
        question: trimmed,
        ...schoolInfo,
      });
      const text = response?.data?.content || 'I could not generate a response right now.';
      setMessages((prev) => [...prev, { role: 'assistant', text }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'Admissions assistant is temporarily unavailable. Please contact the school office.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        onClick={() => setOpen((prev) => !prev)}
        sx={{ position: 'fixed', right: 18, bottom: 18, zIndex: 1700 }}
      >
        <SmartToyRoundedIcon />
      </Fab>

      {open ? (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: 18,
            bottom: 82,
            width: { xs: 'calc(100vw - 28px)', sm: 360 },
            maxHeight: 520,
            zIndex: 1700,
            p: 1.2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.5, pb: 0.8 }}>
            <Typography variant="subtitle2" fontWeight={700}>AI Admissions Assistant</Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1, mb: 1, height: 340, overflowY: 'auto' }}>
            <Stack spacing={1}>
              {messages.map((item, idx) => (
                <Box
                  key={`${item.role}-${idx}`}
                  sx={{
                    alignSelf: item.role === 'user' ? 'flex-end' : 'flex-start',
                    bgcolor: item.role === 'user' ? 'primary.main' : 'grey.100',
                    color: item.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    px: 1,
                    py: 0.8,
                    borderRadius: 1.2,
                    maxWidth: '92%',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.82rem',
                  }}
                >
                  {item.text}
                </Box>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about admissions..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <IconButton color="primary" onClick={send} disabled={sending || !question.trim()}>
              <SendRoundedIcon />
            </IconButton>
          </Stack>
        </Paper>
      ) : null}
    </>
  );
};

export default AiAdmissionsChatbot;
